import { useState } from 'react';
import { ActionErrorAlert } from '@/components/ActionErrorAlert';
import { useActionError, type ActionError } from '@/components/useActionError';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useTenantApi } from '@/tenant/useTenant';
import { useToast } from '@/components/useToast';
import { CardCapture } from '@/features/billing/CardCapture';
import { isCardDeclinedCode } from '@/features/billing/cardDeclined';
import { generateRequestId } from '@/utils/uuid';
import { isSubscriptionSetupConflict, subscriberErrorMessage } from './subscriberErrors';

export interface UncompDialogProps {
  open: boolean;
  onClose: () => void;
  onDone: (opts?: { quiet?: boolean }) => void;
  subscriptionId: number;
}

export function UncompDialog({ open, onClose, onDone, subscriptionId }: UncompDialogProps) {
  const { t } = useTranslation();
  const showToast = useToast();
  const api = useTenantApi();
  const describe = useActionError(subscriberErrorMessage);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<ActionError | null>(null);
  const [cardEpoch, setCardEpoch] = useState(0);

  async function handleToken(token: string): Promise<void | 'retry'> {
    setSubmitting(true);
    setError(null);
    try {
      await api.uncomp(subscriptionId, {
        cardToken: token,
        idempotencyKey: generateRequestId(),
      });
      showToast(t('subscribers.toast.uncomped'));
      onDone();
      onClose();
    } catch (err) {
      setError(describe(err));
      if (isSubscriptionSetupConflict(err)) {
        onDone({ quiet: true });
        setCardEpoch((epoch) => epoch + 1);
        return;
      }
      if (isCardDeclinedCode(err)) return 'retry';
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose}>
      <DialogTitle>{t('subscribers.uncomp.title')}</DialogTitle>
      <DialogContent>
        <ActionErrorAlert error={error} sx={{ mb: 2 }} />
        <CardCapture
          key={cardEpoch}
          scope="tenant"
          submitLabel={t('subscribers.actions.removeComp')}
          busy={submitting}
          onToken={handleToken}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t('common.cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
